/**
 * Ground shadows of figures, beasts and props, in pixel art rather than smooth ellipses:
 *
 *   - the contact shadow right under the feet: a solid core and a dithered rim (every other pixel),
 *     so its edge is soft without any blended pixels;
 *   - the cast shadow the sun throws away from the key light (upper right), lying along the iso
 *     ground toward the lower left: solid near the feet, dithered out toward its tip.
 *
 * Shadows lean cool (indigo), and violet in the low sun of dusk and dawn. Each size and tint is
 * drawn once and kept; a shadow is tilted to lie along sloping ground (a stair, a roof) by shearing
 * it, still nearest-neighbour.
 */
import { canvas, ctx2d } from './pixel';

/** Shadow colours by tint: 0 the day's cool shade, 1 golden hour, 2 the violet of dusk. */
export const TINTS = ['#161428', '#1e1530', '#261436'] as const;
export type ShadowTint = 0 | 1 | 2;

/** 4×4 ordered dither threshold (0..1) at a pixel. */
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
const bayer = (x: number, y: number): number => (BAYER[(y & 3) * 4 + (x & 3)]! + 0.5) / 16;

const CONTACT = new Map<number, HTMLCanvasElement>();

/** The contact shadow of half-width `rx` and half-height `ry` (whole pixels). */
function contactSprite(rx: number, ry: number, tint: ShadowTint): HTMLCanvasElement {
  const key = (rx * 1024 + ry) * 4 + tint;
  let c = CONTACT.get(key);
  if (c) return c;
  const w = rx * 2 + 1;
  const h = ry * 2 + 1;
  c = canvas(w, h);
  const g = ctx2d(c);
  g.fillStyle = TINTS[tint];
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const dx = (x - rx) / (rx + 0.5);
      const dy = (y - ry) / (ry + 0.5);
      const d = dx * dx + dy * dy;
      if (d > 1) continue;
      // The core solid; the outer band dithered, a little more of it toward the core.
      const rim = d > 0.55;
      if (rim && (x + y) % 2 && !(d < 0.72 && x % 2)) continue;
      g.fillRect(x, y, 1, 1);
    }
  CONTACT.set(key, c);
  return c;
}

/** Screen direction of the cast shadow along the iso ground (2:1), toward the lower left. */
const AX = -2 / Math.sqrt(5);
const AY = 1 / Math.sqrt(5);

const CAST = new Map<number, { img: HTMLCanvasElement; ox: number; oy: number }>();

/**
 * The cast shadow `len` screen pixels long and `half` wide each side, starting at the feet. Its
 * outline is a body's: a rounded heel under the feet, the width held up the length and a rounded
 * end where the head falls; the last part is dithered out, as a shadow grows paler further from
 * what casts it.
 */
function castSprite(
  len: number,
  half: number,
  tint: ShadowTint,
): { img: HTMLCanvasElement; ox: number; oy: number } {
  const key = (len * 64 + half) * 4 + tint;
  let s = CAST.get(key);
  if (s) return s;
  const heel = Math.max(1, half * 0.6);
  // Screen extent: the corners of the shape's box along the shadow (u) and across the ground (v).
  const xs: number[] = [];
  const ys: number[] = [];
  for (const u of [-heel, len])
    for (const v of [-half, half]) {
      // `v` runs along the other ground axis, (2, 1) on screen.
      xs.push(u * AX - v * AX);
      ys.push(u * AY + v * AY);
    }
  const x0 = Math.floor(Math.min(...xs)) - 1;
  const y0 = Math.floor(Math.min(...ys)) - 1;
  const w = Math.ceil(Math.max(...xs)) - x0 + 2;
  const h = Math.ceil(Math.max(...ys)) - y0 + 2;
  const c = canvas(w, h);
  const g = ctx2d(c);
  g.fillStyle = TINTS[tint];
  const cap = Math.min(half, len * 0.5);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const dx = x + 0.5 + x0;
      const dy = y + 0.5 + y0;
      // Back from screen to the shadow's own axes.
      const u = (dx / AX + dy / AY) / 2;
      const v = (dy / AY - dx / AX) / 2;
      // Narrower toward the tip, as the shoulders and head are narrower than the spread of a stance.
      const wide = half * (1 - 0.3 * Math.max(0, u / len));
      let inside: boolean;
      if (u < 0) inside = (u / heel) ** 2 + (v / half) ** 2 <= 1;
      else if (u > len - cap) inside = ((u - (len - cap)) / cap) ** 2 + (v / wide) ** 2 <= 1;
      else inside = Math.abs(v) <= wide;
      if (!inside) continue;
      // Dithered out over the far 45% of its length.
      const t = u / len;
      if (t > 0.55 && bayer(x, y) < ((t - 0.55) / 0.45) * 0.8) continue;
      g.fillRect(x, y, 1, 1);
    }
  s = { img: c, ox: -x0, oy: -y0 };
  CAST.set(key, s);
  return s;
}

/**
 * Draws a contact shadow centred at (`x`, `y`), `rx` by `ry` screen pixels, at `alpha`, sheared by
 * `tilt` (screen pixels down per pixel right) to lie on sloping ground.
 */
export function drawContactShadow(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  rx: number,
  ry: number,
  alpha: number,
  tilt = 0,
  tint: ShadowTint = 0,
): void {
  const R = Math.max(1, Math.round(rx));
  const r = Math.max(1, Math.round(ry));
  const img = contactSprite(R, r, tint);
  g.save();
  g.imageSmoothingEnabled = false;
  g.globalAlpha = alpha;
  g.translate(Math.round(x), Math.round(y));
  if (tilt) g.transform(1, Math.max(-1, Math.min(1, tilt)), 0, 1, 0, 0);
  g.drawImage(img, -R, -r);
  g.restore();
}

/**
 * Draws a cast shadow from the feet at (`x`, `y`) toward the lower left, `len` screen pixels long
 * (rounded to even pixels so it does not change size every frame as the hour turns) and `half`
 * wide each side, at `alpha`, sheared by `tilt` like the contact shadow.
 */
export function drawCastShadow(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  len: number,
  half: number,
  alpha: number,
  tilt = 0,
  tint: ShadowTint = 0,
): void {
  const L = Math.max(2, Math.round(len / 2) * 2);
  const W = Math.max(1, Math.round(half));
  const s = castSprite(L, W, tint);
  g.save();
  g.imageSmoothingEnabled = false;
  g.globalAlpha = alpha;
  g.translate(Math.round(x), Math.round(y));
  if (tilt) g.transform(1, Math.max(-1, Math.min(1, tilt)), 0, 1, 0, 0);
  g.drawImage(s.img, -s.ox, -s.oy);
  g.restore();
}
